import * as React from "react";
import { t } from "../../i18n";
import { FormComponentProps } from "../form/Form";
import { FormGroup } from "../form/FormGroup";
import { FormInput } from "../form/FormInput";
import { FormTransferContainer } from "../form/FormTransferContainer";

/**
 * The effect flags are deliberately not named `model`, `valid`, `modelValue` or `fieldSet`: the
 * parent form clones those names onto every child it walks, which would overwrite them.
 */
interface GoogleOneTapCardProps extends FormComponentProps {
  oneTapEnabled?: boolean;
  googleClientIdConfigured?: boolean;
  ssoStatusUnavailable?: boolean;
  ssoStatusPending?: boolean;
}

/**
 * A card rendered inside the Integration Settings `<Form>`. The form wires each
 * `model`-bound input by walking its own literal child tree, and that descent
 * stops at a component boundary — the inputs below live in this component's
 * render output, which the parent form never sees. The parent still injects its
 * `fieldSet` here as a prop, so `FormTransferContainer` re-runs the binding
 * descent over these children with that fieldSet, exactly as `FormMap` does.
 */
export function GoogleOneTapCard(props: GoogleOneTapCardProps): JSX.Element {
  const effectiveStatusKnowable = props.oneTapEnabled === true && props.ssoStatusPending !== true;
  const inactive = effectiveStatusKnowable && !props.ssoStatusUnavailable && props.googleClientIdConfigured === false;
  const effectiveStatusUnknown = effectiveStatusKnowable && (props.ssoStatusUnavailable === true || props.googleClientIdConfigured === undefined);
  return (
    <div className={"card"}>
      <div className={"card-header"}>
        <h4>Google One Tap</h4>
        <span className={"help-block"}>
          {t(
            "integrations.google-one-tap.description",
            undefined,
            "Google One Tap shows a Google sign-in prompt to players so they can register or log in with their Google account.",
          )}{" "}
          <a
            href={"https://developers.google.com/identity/gsi/web/guides/features"}
            target={"_blank"}
            rel={"noopener noreferrer"}
          >
            Google Identity Services
          </a>
        </span>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-lg-12">
            <FormTransferContainer fieldSet={props.fieldSet}>
              <FormGroup
                className="custom-control custom-checkbox"
                model="oneTapEnabled"
              >
                <FormInput
                  className="custom-control-input"
                  model="oneTapEnabled"
                  id={"oneTapEnabled"}
                  data-testid={"one-tap-enabled-checkbox"}
                  type="checkbox"
                />
                <label
                  htmlFor={"oneTapEnabled"}
                  className={"custom-control-label"}
                >
                  {t("integrations.google-one-tap.enable-label", undefined, "Enable Google One Tap")}
                </label>
                <br />
              </FormGroup>
            </FormTransferContainer>
            <small
              className={"form-text text-muted mb-2"}
              data-testid={"one-tap-client-id-note"}
            >
              {t(
                "integrations.google-one-tap.client-id-note",
                undefined,
                "Uses the same Google client id as Google login. See the Google row under Single Sign-On.",
              )}
            </small>
            {inactive && (
              <span
                className={"badge p-1 badge-warning"}
                data-testid={"one-tap-inactive-badge"}
              >
                {t("integrations.google-one-tap.inactive-badge", undefined, "Inactive — no Google client id configured")}
              </span>
            )}
            {effectiveStatusUnknown && (
              <span
                className={"text-muted"}
                data-testid={"one-tap-status-unavailable"}
              >
                {t("integrations.google-one-tap.status-unavailable", undefined, "Effective status unavailable.")}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
